import React from "react";

import {
  Box,
  IconButton,
  Link,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Theme } from "@material-ui/core/styles";
import { ArrowForward } from "@material-ui/icons";

import {
  BENEFITS_ACCENT_COLOR,
  benefitsBannerContent,
  benefitsBannerImage,
} from "../../content/benefitsBannerContent";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: "relative",
    overflow: "hidden",
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: "#FFF",
    boxShadow: "0rem 0.25rem 0.90rem 0rem rgba(176, 193, 225, 0.25)",
    textDecoration: "none",
    cursor: "pointer",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: "0rem 0.375rem 1.25rem 0rem rgba(176, 193, 225, 0.45)",
      transform: "translateY(-2px)",
    },
    [theme.breakpoints.only("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
      textAlign: "left",
      padding: 0,
      gap: 0,
    },
  },
  imageLayer: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    width: "30%",
    backgroundImage: `url(${benefitsBannerImage})`,
    backgroundSize: "cover",
    backgroundPosition: "left 28%",
    backgroundRepeat: "no-repeat",
    WebkitMaskImage:
      "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 45%, rgba(0,0,0,0) 100%)",
    maskImage:
      "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 45%, rgba(0,0,0,0) 100%)",
    pointerEvents: "none",
    [theme.breakpoints.only("xs")]: {
      position: "relative",
      width: "100%",
      height: 160,
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12,
      backgroundPosition: "center top 30%",
      WebkitMaskImage:
        "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)",
      maskImage:
        "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)",
    },
  },
  content: {
    position: "relative",
    zIndex: 1,
    flex: 1,
    minWidth: 0,
    paddingLeft: "30%",
    [theme.breakpoints.only("xs")]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      marginTop: theme.spacing(-2),
    },
  },
  title: {
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "28px",
    color: BENEFITS_ACCENT_COLOR,
    marginBottom: theme.spacing(1),
  },
  description: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 400,
    fontSize: "15px",
    lineHeight: "21px",
    color: "#3E3D3D",
    opacity: 0.7,
  },
  arrowWrapper: {
    position: "relative",
    zIndex: 1,
    flexShrink: 0,
    [theme.breakpoints.only("xs")]: {
      alignSelf: "flex-end",
      marginTop: theme.spacing(1),
      paddingRight: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
  },
  arrowButton: {
    color: BENEFITS_ACCENT_COLOR,
    backgroundColor: "rgba(217, 31, 5, 0.08)",
    "&:hover": {
      backgroundColor: "rgba(217, 31, 5, 0.16)",
    },
  },
}));

/**
 * Faixa full-width da campanha de benefícios, exibida entre o título da Home e
 * a grade de ações. A faixa inteira é um link para o site de marketing (abre em
 * nova aba); o ícone de seta é apenas uma affordance visual do clique.
 */
const BenefitsBannerStrip: React.FC = () => {
  const classes = useStyles();

  const {
    title,
    description_1,
    description_2,
    ctaLabel,
    ctaHref,
  } = benefitsBannerContent;

  return (
    <Link
      className={classes.root}
      href={ctaHref}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ctaLabel}
      underline="none"
    >
      <Box
        className={classes.imageLayer}
        role="img"
        aria-label="Programa de benefícios Atem"
      />
      <Box className={classes.content}>
        <Typography className={classes.title}>{title}</Typography>
        <Typography className={classes.description}>{description_1}</Typography>
        <Typography className={classes.description}>{description_2}</Typography>
      </Box>
      <Box className={classes.arrowWrapper}>
        <Tooltip title={ctaLabel}>
          <IconButton
            className={classes.arrowButton}
            aria-label={ctaLabel}
            component="span"
          >
            <ArrowForward />
          </IconButton>
        </Tooltip>
      </Box>
    </Link>
  );
};

export default BenefitsBannerStrip;
